import { averageFrameDifference } from './cameraSetup';

export interface MonitoringFrameSample {
  luminance: number;
  variance: number;
  pixels: Uint8ClampedArray;
  grayscale: Uint8Array;
}

export interface SceneSignature {
  blocks: Float32Array;
  histogram: Float32Array;
  luminance: number;
  edgeStrength: number;
}

export function readMonitoringFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): MonitoringFrameSample | undefined {
  if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) return undefined;
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return undefined;
  context.drawImage(video, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height).data;
  let total = 0;
  let squaredTotal = 0;
  const count = pixels.length / 4;
  const grayscale = new Uint8Array(count);
  for (let index = 0; index < pixels.length; index += 4) {
    const luminance = pixels[index] * 0.2126 + pixels[index + 1] * 0.7152 + pixels[index + 2] * 0.0722;
    grayscale[index / 4] = Math.round(luminance);
    total += luminance;
    squaredTotal += luminance * luminance;
  }
  const luminance = total / count;
  return { luminance, variance: Math.max(0, squaredTotal / count - luminance * luminance), pixels, grayscale };
}

export function createSceneSignature(sample: MonitoringFrameSample, width: number, height: number, blockColumns = 8, blockRows = 6): SceneSignature {
  const blocks = new Float32Array(blockColumns * blockRows);
  const blockCounts = new Uint16Array(blocks.length);
  const histogram = new Float32Array(16);
  let edges = 0;
  let edgeCount = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      const value = sample.grayscale[index];
      const blockX = Math.min(blockColumns - 1, Math.floor((x / width) * blockColumns));
      const blockY = Math.min(blockRows - 1, Math.floor((y / height) * blockRows));
      const blockIndex = blockY * blockColumns + blockX;
      blocks[blockIndex] += value;
      blockCounts[blockIndex] += 1;
      histogram[Math.min(15, Math.floor(value / 16))] += 1;
      if (x > 0) { edges += Math.abs(value - sample.grayscale[index - 1]); edgeCount += 1; }
      if (y > 0) { edges += Math.abs(value - sample.grayscale[index - width]); edgeCount += 1; }
    }
  }
  for (let index = 0; index < blocks.length; index += 1) blocks[index] /= blockCounts[index] || 1;
  for (let index = 0; index < histogram.length; index += 1) histogram[index] /= sample.grayscale.length || 1;
  return { blocks, histogram, luminance: sample.luminance, edgeStrength: edgeCount ? edges / edgeCount : 0 };
}

export function frameDifference(previous: Uint8ClampedArray | undefined, current: Uint8ClampedArray): number {
  return averageFrameDifference(previous, current);
}

export function frameHealthConditions(sample: MonitoringFrameSample, thresholds: {
  darkLuminanceThreshold: number;
  brightLuminanceThreshold: number;
  obstructionLuminanceThreshold: number;
  obstructionVarianceThreshold: number;
}) {
  const obstructed = sample.luminance < thresholds.obstructionLuminanceThreshold && sample.variance < thresholds.obstructionVarianceThreshold;
  return {
    obstructed,
    dark: !obstructed && sample.luminance < thresholds.darkLuminanceThreshold,
    overexposed: sample.luminance > thresholds.brightLuminanceThreshold && sample.variance < thresholds.obstructionVarianceThreshold
  };
}
